import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://localhost:8787/api'
);

const statsApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

/**
 * Record a card flip for a node
 * @param {number} nodeId - The node ID being flipped
 * @returns {Promise<{success: boolean, nodeId: number, countryCode: string, isAuthenticated: boolean}>}
 */
export const recordFlip = async (nodeId) => {
  try {
    const response = await statsApi.post(`/stats/flip/${nodeId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to record flip:', error);
    // Don't throw - flip recording failures shouldn't break the UI
    return { success: false };
  }
};

/**
 * Get flip counts for a node (admin only)
 * @param {number} nodeId - The node ID
 * @returns {Promise<{nodeId: number, anonymousCount: number, authenticatedCount: number}>}
 */
export const getFlipCounts = async (nodeId) => {
  try {
    const response = await statsApi.get(`/stats/flips/${nodeId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get flip counts:', error);
    return { nodeId, anonymousCount: 0, authenticatedCount: 0 };
  }
};

/**
 * Get country breakdown for flip stats (admin only)
 * @param {number} nodeId - The node ID
 * @param {string} type - 'anonymous' or 'authenticated'
 * @returns {Promise<{nodeId: number, type: string, countries: Array<{countryCode: string, count: number}>}>}
 */
export const getCountryStats = async (nodeId, type = 'anonymous') => {
  try {
    const response = await statsApi.get(`/stats/countries/${nodeId}?type=${type}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get country stats:', error);
    return { nodeId, type, countries: [] };
  }
};

/**
 * Get total flip counts across all nodes (admin only)
 * @returns {Promise<{anonymousCount: number, authenticatedCount: number}>}
 */
export const getTotalFlipCounts = async () => {
  try {
    const response = await statsApi.get('/stats/flips/total');
    return response.data;
  } catch (error) {
    console.error('Failed to get total flip counts:', error);
    return { anonymousCount: 0, authenticatedCount: 0 };
  }
};

/**
 * Get total country breakdown for flip stats across all nodes (admin only)
 * @param {string} type - 'anonymous' or 'authenticated'
 * @returns {Promise<{type: string, countries: Array<{countryCode: string, count: number}>}>}
 */
export const getTotalCountryStats = async (type = 'anonymous') => {
  try {
    const response = await statsApi.get(`/stats/countries/total?type=${type}`);
    return response.data;
  } catch (error) {
    console.error('Failed to get total country stats:', error);
    return { type, countries: [] };
  }
};

export default statsApi;
